import InstagramLogo from "@/assets/images/platforms/instagram.webp";
import TikTokLogo from "@/assets/images/platforms/tiktok.webp";
import {
  INSTAGRAM_CLIENT_ID,
  INSTAGRAM_REDIRECT_URI,
  TIKTOK_CLIENT_ID,
  TIKTOK_REDIRECT_URI,
} from "@/constants/extras";
import { Avatar, AvatarImage } from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatNumber,
  formatPercentage,
} from "@repo/components/src/utils/numbers";
import {
  getInstagramAuthUrl,
  getTikTokAuthUrl,
} from "@repo/components/src/utils/socialNetworkUser";
import {
  CampaignAnalyticFragmentFragment,
  PlatformImpactFragmentFragment,
} from "@repo/graphql/documents/campaign/fragment.generated";
import { Plus } from "lucide-react-native";
import React, { useMemo, useState } from "react";
import { Linking } from "react-native";
import { CHART_COLORS } from "../shared/chartTokens";
import IntegrationCard from "../shared/integrationCard";
import Section from "../shared/section";
import Sparkline from "../shared/sparkline";
import { formatRangeWindow } from "../shared/utils";

const EMPTY_GRADIENT =
  "linear-gradient(135deg, #f4efff 0%, #ffffff 60%, #fde4f1 100%)";

type PlatformKey = "total" | "instagram" | "tiktok";
type DayPoint = PlatformImpactFragmentFragment["daily"][number];

type Props = {
  report: CampaignAnalyticFragmentFragment;
  brandId?: string;
};

/** Campaign and baseline values for one metric, as a daily rate plus its total. */
type Metric = {
  campaignTotal: number;
  campaignPerDay: number;
  baselinePerDay: number;
};

/**
 * A platform's numbers reduced to what the cards render. Campaign and baseline
 * windows are different lengths, so only the daily rates may be compared; the
 * totals are shown as-is because they are what actually happened.
 */
type ImpactView = {
  handle: string;
  currentFollowers: number;
  campaignDays: number;
  baselineDays: number;
  followers: Metric;
  profileViews: Metric;
  reach: Metric;
  interactions: Metric;
  linkClicks: Metric;
  engagementRateCampaign: number;
  engagementRateBaseline: number;
  daily: DayPoint[];
};

const toView = (p: PlatformImpactFragmentFragment): ImpactView => ({
  handle: p.handle,
  currentFollowers: p.currentFollowers,
  campaignDays: p.campaignDays,
  baselineDays: p.baselineDays,
  followers: {
    campaignTotal: p.followersGainedCampaign,
    campaignPerDay: p.followersGainedCampaignPerDay,
    baselinePerDay: p.followersGainedBaselinePerDay,
  },
  profileViews: {
    campaignTotal: p.profileViewsCampaign,
    campaignPerDay: p.profileViewsCampaignPerDay,
    baselinePerDay: p.profileViewsBaselinePerDay,
  },
  reach: {
    campaignTotal: p.reachCampaign,
    campaignPerDay: p.reachCampaignPerDay,
    baselinePerDay: p.reachBaselinePerDay,
  },
  interactions: {
    campaignTotal: p.interactionsCampaign,
    campaignPerDay: p.interactionsCampaignPerDay,
    baselinePerDay: p.interactionsBaselinePerDay,
  },
  linkClicks: {
    campaignTotal: p.linkClicksCampaign,
    campaignPerDay: p.linkClicksCampaignPerDay,
    baselinePerDay: p.linkClicksBaselinePerDay,
  },
  engagementRateCampaign: p.engagementRateCampaign,
  engagementRateBaseline: p.engagementRateBaseline,
  daily: [...p.daily],
});

const addMetric = (a: Metric, b: Metric): Metric => ({
  campaignTotal: a.campaignTotal + b.campaignTotal,
  campaignPerDay: a.campaignPerDay + b.campaignPerDay,
  baselinePerDay: a.baselinePerDay + b.baselinePerDay,
});

const sumDaily = (a: DayPoint[], b: DayPoint[]): DayPoint[] => {
  const byDay = new Map<string, DayPoint>();
  [...a, ...b].forEach((p) => {
    const prev = byDay.get(p.day);
    if (!prev) {
      byDay.set(p.day, { ...p });
      return;
    }
    byDay.set(p.day, {
      __typename: "BrandImpactDayPointType",
      day: p.day,
      followers: prev.followers + p.followers,
      profileViews: prev.profileViews + p.profileViews,
      reach: prev.reach + p.reach,
      interactions: prev.interactions + p.interactions,
      linkClicks: prev.linkClicks + p.linkClicks,
    });
  });
  return Array.from(byDay.values()).sort((x, y) => (x.day < y.day ? -1 : 1));
};

/**
 * Combine the platforms that actually reported data. Daily rates add across
 * platforms because both share the campaign's windows.
 */
const combine = (views: ImpactView[]): ImpactView | null => {
  if (views.length === 0) return null;
  if (views.length === 1) return views[0]!;
  const [first, ...rest] = views;
  const merged = rest.reduce<ImpactView>(
    (acc, view) => ({
      ...acc,
      handle: "ambos canales",
      currentFollowers: acc.currentFollowers + view.currentFollowers,
      followers: addMetric(acc.followers, view.followers),
      profileViews: addMetric(acc.profileViews, view.profileViews),
      reach: addMetric(acc.reach, view.reach),
      interactions: addMetric(acc.interactions, view.interactions),
      linkClicks: addMetric(acc.linkClicks, view.linkClicks),
      daily: sumDaily(acc.daily, view.daily),
    }),
    { ...first, handle: "ambos canales" },
  );
  return {
    ...merged,
    engagementRateCampaign:
      merged.reach.campaignPerDay > 0
        ? merged.interactions.campaignPerDay / merged.reach.campaignPerDay
        : 0,
    engagementRateBaseline:
      merged.reach.baselinePerDay > 0
        ? merged.interactions.baselinePerDay / merged.reach.baselinePerDay
        : 0,
  };
};

const isoDay = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const formatSignedNumber = (value: number): string => {
  const formatted = formatNumber(Math.abs(value), 0);
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return "0";
};

const formatRate = (value: number): string => formatNumber(value, 1);

type Delta = { text: string; positive: boolean | null };

/**
 * Compare two daily rates. Returns a null verdict when there is nothing to
 * compare, so the card can stay silent instead of claiming a spurious "↑ 0 %".
 */
const rateDelta = (campaignPerDay: number, baselinePerDay: number): Delta => {
  if (baselinePerDay <= 0 && campaignPerDay <= 0) {
    return { text: "sin actividad", positive: null };
  }
  if (baselinePerDay <= 0) {
    return { text: "sin línea base", positive: true };
  }
  const ratio = (campaignPerDay - baselinePerDay) / baselinePerDay;
  if (ratio >= 1) {
    return {
      text: `${formatNumber(campaignPerDay / baselinePerDay, 1)}× vs línea base`,
      positive: true,
    };
  }
  return {
    text: formatPercentage(Math.abs(ratio)),
    positive: campaignPerDay >= baselinePerDay,
  };
};

/** Prefer the combined view, else whichever single platform reported data. */
const defaultPlatform = (
  igData: PlatformImpactFragmentFragment | null,
  ttData: PlatformImpactFragmentFragment | null,
): PlatformKey => {
  if (igData && ttData) return "total";
  if (igData) return "instagram";
  if (ttData) return "tiktok";
  return "total";
};

const hasDataFor = (
  platform: PlatformKey,
  igData: PlatformImpactFragmentFragment | null,
  ttData: PlatformImpactFragmentFragment | null,
): boolean => {
  if (platform === "instagram") return !!igData;
  if (platform === "tiktok") return !!ttData;
  return !!(igData || ttData);
};

const BrandImpactSection = React.memo(({ report, brandId }: Props) => {
  const impact = report.brandSocialImpact;
  const ig = impact.instagram;
  const tt = impact.tiktok;

  // A linked account whose token has died still appears here, but with no
  // series behind it. Treat it as absent for every numeric purpose.
  const igData = ig?.hasData ? ig : null;
  const ttData = tt?.hasData ? tt : null;

  const total = useMemo(
    () => combine([igData, ttData].filter(Boolean).map((p) => toView(p!))),
    [igData, ttData],
  );

  const [selected, setSelected] = useState<PlatformKey>(() =>
    defaultPlatform(igData, ttData),
  );

  // Refreshing the metrics writes new data into the same cache entry without
  // remounting this section, so the selected tab can lose its data after mount.
  // Resolve the tab during render rather than storing a selection that has gone
  // stale: a tab that still has data is honoured, one that emptied falls back.
  const platform = hasDataFor(selected, igData, ttData)
    ? selected
    : defaultPlatform(igData, ttData);

  if (!impact || !impact.hasData) return null;
  if (!ig && !tt && !brandId) return null;

  const igAuthUrl = brandId
    ? getInstagramAuthUrl(brandId, INSTAGRAM_REDIRECT_URI, INSTAGRAM_CLIENT_ID)
    : "";
  const ttAuthUrl = brandId
    ? getTikTokAuthUrl(brandId, TIKTOK_REDIRECT_URI, TIKTOK_CLIENT_ID)
    : "";

  const activeView =
    platform === "instagram"
      ? igData && toView(igData)
      : platform === "tiktok"
        ? ttData && toView(ttData)
        : total;

  const campaignRange = formatRangeWindow(
    impact.campaignWindow.start,
    impact.campaignWindow.ongoing ? null : impact.campaignWindow.end,
  );
  const baselineRange = formatRangeWindow(
    impact.baselineWindow.start,
    impact.baselineWindow.end,
  );
  const campaignStartDay = isoDay(impact.campaignWindow.start);

  return (
    <Section
      title="Impacto en redes de la marca"
      description="Métricas propias de la marca durante la campaña frente a los 14 días previos al primer post. Como las ventanas duran distinto, la comparación se hace sobre promedios diarios."
    >
      <HStack space="lg" className="items-stretch">
        <SideRail
          ig={ig}
          tt={tt}
          platform={platform}
          onPlatform={setSelected}
          baselineRange={baselineRange}
          campaignRange={campaignRange}
          igAuthUrl={igAuthUrl}
          ttAuthUrl={ttAuthUrl}
        />
        <Box className="flex-1 min-w-0">
          {activeView ? (
            <Grid data={activeView} campaignStartDay={campaignStartDay} />
          ) : (
            <PlatformEmpty
              platform={platform}
              ig={ig}
              tt={tt}
              igAuthUrl={igAuthUrl}
              ttAuthUrl={ttAuthUrl}
            />
          )}
        </Box>
      </HStack>
    </Section>
  );
});

BrandImpactSection.displayName = "BrandImpactSection";

const openExternal = (url: string) => {
  if (!url) return;
  if (typeof window !== "undefined") {
    window.open(url, "_blank");
    return;
  }
  Linking.openURL(url);
};

const SideRail = ({
  ig,
  tt,
  platform,
  onPlatform,
  baselineRange,
  campaignRange,
  igAuthUrl,
  ttAuthUrl,
}: {
  ig: PlatformImpactFragmentFragment | null;
  tt: PlatformImpactFragmentFragment | null;
  platform: PlatformKey;
  onPlatform: (p: PlatformKey) => void;
  baselineRange: string;
  campaignRange: string;
  igAuthUrl: string;
  ttAuthUrl: string;
}) => (
  <VStack
    space="md"
    className="bg-background-50 rounded-2xl p-5 border border-outline-100"
    style={{ width: 260, flexShrink: 0 } as object}
  >
    <Text
      size="2xs"
      className="text-typography-500 uppercase font-bold tracking-wide"
    >
      Cuentas analizadas
    </Text>
    <AccountRow
      logo={InstagramLogo}
      platform={ig}
      fallbackHandle="Sin Instagram"
      connectLabel="Conectar Instagram"
      connectUrl={igAuthUrl}
    />
    <AccountRow
      logo={TikTokLogo}
      platform={tt}
      fallbackHandle="Sin TikTok"
      connectLabel="Conectar TikTok"
      connectUrl={ttAuthUrl}
    />

    <Box className="h-px bg-outline-100 my-1" />

    <Text
      size="2xs"
      className="text-typography-500 uppercase font-bold tracking-wide"
    >
      Mostrando
    </Text>
    <PlatformToggle
      value={platform}
      onChange={onPlatform}
      hasIg={!!ig?.hasData}
      hasTt={!!tt?.hasData}
    />

    <Box className="h-px bg-outline-100 my-1" />

    <VStack space="xs">
      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        Línea base
      </Text>
      <Text size="xs" className="text-typography-700">
        {baselineRange}
      </Text>
    </VStack>
    <VStack space="xs">
      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        Campaña
      </Text>
      <Text size="xs" className="text-typography-700">
        {campaignRange}
      </Text>
    </VStack>
  </VStack>
);

const AccountRow = ({
  logo,
  platform,
  fallbackHandle,
  connectLabel,
  connectUrl,
}: {
  logo: number;
  platform: PlatformImpactFragmentFragment | null;
  fallbackHandle: string;
  connectLabel: string;
  connectUrl: string;
}) => {
  const connected = !!platform;
  const stale = !!platform && !platform.hasData;
  return (
    <HStack space="sm" className="items-center">
      <Avatar size="sm">
        <AvatarImage source={logo} />
      </Avatar>
      <VStack className="flex-1 min-w-0">
        <Text
          size="xs"
          className={
            connected
              ? "text-typography-900 font-semibold"
              : "text-typography-400"
          }
          numberOfLines={1}
        >
          {connected ? `@${platform!.handle}` : fallbackHandle}
        </Text>
        {!connected ? (
          <Text size="2xs" className="text-typography-400">
            No conectado
          </Text>
        ) : stale ? (
          <Text size="2xs" className="text-warning-600">
            Sin datos en esta ventana
          </Text>
        ) : (
          <Text size="2xs" className="text-typography-500">
            {formatNumber(platform!.currentFollowers, 0)} followers
          </Text>
        )}
      </VStack>
      {!connected && connectUrl ? (
        <Button
          size="xs"
          rounded="full"
          variant="solid"
          action="brand"
          onPress={() => openExternal(connectUrl)}
          aria-label={connectLabel}
          className="w-7 h-7 p-0"
        >
          <ButtonIcon as={Plus} />
        </Button>
      ) : null}
    </HStack>
  );
};

const PlatformToggle = ({
  value,
  onChange,
  hasIg,
  hasTt,
}: {
  value: PlatformKey;
  onChange: (p: PlatformKey) => void;
  hasIg: boolean;
  hasTt: boolean;
}) => {
  const options: {
    key: PlatformKey;
    label: string;
    disabled: boolean;
    tooltip?: string;
  }[] = [
    {
      key: "total",
      label: "Total",
      disabled: !hasIg && !hasTt,
      tooltip:
        !hasIg && !hasTt
          ? "Conecta al menos una cuenta con datos para ver el total."
          : undefined,
    },
    {
      key: "instagram",
      label: "IG",
      disabled: !hasIg,
      tooltip: !hasIg ? "Sin datos de Instagram para esta ventana" : undefined,
    },
    {
      key: "tiktok",
      label: "TT",
      disabled: !hasTt,
      tooltip: !hasTt ? "Sin datos de TikTok para esta ventana" : undefined,
    },
  ];
  return (
    <HStack
      space="xs"
      className="bg-background-0 p-1 rounded-lg border border-outline-100"
    >
      {options.map((opt) => {
        const active = value === opt.key;
        const button = (
          <Pressable
            onPress={() => !opt.disabled && onChange(opt.key)}
            className={
              "w-full px-2 py-1.5 rounded-md " +
              (active && !opt.disabled
                ? "bg-brand-500"
                : opt.disabled
                  ? "bg-background-50"
                  : "bg-transparent")
            }
            disabled={opt.disabled}
          >
            <Text
              size="xs"
              className={
                "text-center font-semibold " +
                (active && !opt.disabled
                  ? "text-typography-white"
                  : opt.disabled
                    ? "text-typography-300"
                    : "text-typography-700")
              }
            >
              {opt.label}
            </Text>
          </Pressable>
        );
        if (opt.tooltip) {
          return (
            <Tooltip
              key={opt.key}
              placement="top"
              trigger={(triggerProps) => (
                <Box {...triggerProps} className="flex-1">
                  {button}
                </Box>
              )}
            >
              <TooltipContent>
                <TooltipText>{opt.tooltip}</TooltipText>
              </TooltipContent>
            </Tooltip>
          );
        }
        return (
          <Box key={opt.key} className="flex-1">
            {button}
          </Box>
        );
      })}
    </HStack>
  );
};

const Grid = ({
  data,
  campaignStartDay,
}: {
  data: ImpactView;
  campaignStartDay: string | null;
}) => (
  <VStack space="md">
    <HStack space="md" className="flex-wrap">
      <ImpactCard
        eyebrow="Followers ganados"
        primary={formatSignedNumber(data.followers.campaignTotal)}
        metric={data.followers}
        chart={
          <Sparkline
            points={data.daily.map((d) => ({ day: d.day, value: d.followers }))}
            campaignStartDay={campaignStartDay}
          />
        }
      />
      <ImpactCard
        eyebrow="Visitas al perfil"
        primary={formatNumber(data.profileViews.campaignTotal, 0)}
        metric={data.profileViews}
        chart={
          <Sparkline
            points={data.daily.map((d) => ({
              day: d.day,
              value: d.profileViews,
            }))}
            campaignStartDay={campaignStartDay}
          />
        }
      />
    </HStack>
    <HStack space="md" className="flex-wrap">
      <ReachEngagementCard data={data} campaignStartDay={campaignStartDay} />
      <ImpactCard
        eyebrow="Clicks en profile link"
        primary={formatNumber(data.linkClicks.campaignTotal, 0)}
        metric={data.linkClicks}
        chart={
          <Sparkline
            points={data.daily.map((d) => ({
              day: d.day,
              value: d.linkClicks,
            }))}
            campaignStartDay={campaignStartDay}
          />
        }
      />
    </HStack>
    <Text size="2xs" className="text-typography-400">
      Campaña de {formatNumber(data.campaignDays, 0)} días vs línea base de{" "}
      {formatNumber(data.baselineDays, 0)} días. Los porcentajes comparan
      promedios diarios; los valores grandes son totales de la campaña.
    </Text>
  </VStack>
);

const DeltaLine = ({ metric }: { metric: Metric }) => {
  const delta = rateDelta(metric.campaignPerDay, metric.baselinePerDay);
  const tone =
    delta.positive === null
      ? "text-typography-400"
      : delta.positive
        ? "text-success-600"
        : "text-error-600";
  const arrow = delta.positive === null ? "" : delta.positive ? "↑ " : "↓ ";
  return (
    <HStack space="sm" className="items-baseline flex-wrap">
      <Text size="xs" className="text-typography-500">
        {formatRate(metric.campaignPerDay)}/día · línea base{" "}
        {formatRate(metric.baselinePerDay)}/día
      </Text>
      <Text size="xs" className={"font-semibold " + tone}>
        {arrow}
        {delta.text}
      </Text>
    </HStack>
  );
};

const ImpactCard = ({
  eyebrow,
  primary,
  metric,
  chart,
}: {
  eyebrow: string;
  primary: string;
  metric: Metric;
  chart?: React.ReactNode;
}) => (
  <Card
    variant="outline"
    className="rounded-xl flex-1"
    size="md"
    style={{ minWidth: 240 } as object}
  >
    <VStack space="sm">
      <Text
        size="2xs"
        className="text-typography-500 uppercase font-bold tracking-wide"
      >
        {eyebrow}
      </Text>
      <Heading size="2xl" className="text-typography-900">
        {primary}
      </Heading>
      <DeltaLine metric={metric} />
      {chart ? <Box className="mt-2">{chart}</Box> : null}
    </VStack>
  </Card>
);

const ReachEngagementCard = ({
  data,
  campaignStartDay,
}: {
  data: ImpactView;
  campaignStartDay: string | null;
}) => {
  const erDelta = data.engagementRateCampaign - data.engagementRateBaseline;
  const hasEr =
    data.engagementRateCampaign > 0 || data.engagementRateBaseline > 0;
  return (
    <Card
      variant="outline"
      className="rounded-xl flex-1"
      size="md"
      style={{ minWidth: 280 } as object}
    >
      <VStack space="sm">
        <Text
          size="2xs"
          className="text-typography-500 uppercase font-bold tracking-wide"
        >
          Alcance y engagement propios
        </Text>
        <HStack space="lg" className="items-end">
          <VStack className="flex-1" space="xs">
            <HStack space="xs" className="items-center">
              <LegendSwatch color={CHART_COLORS.primary} />
              <Text size="xs" className="text-typography-500">
                Alcance
              </Text>
            </HStack>
            <Heading size="lg" className="text-typography-900">
              {formatNumber(data.reach.campaignTotal, 0)}
            </Heading>
          </VStack>
          <VStack className="flex-1" space="xs">
            <HStack space="xs" className="items-center">
              <LegendSwatch color={CHART_COLORS.secondary} dashed />
              <Text size="xs" className="text-typography-500">
                Engagement rate
              </Text>
            </HStack>
            <Heading size="lg" className="text-typography-900">
              {hasEr ? formatPercentage(data.engagementRateCampaign) : "—"}
            </Heading>
            {hasEr ? (
              <Text
                size="xs"
                className={erDelta >= 0 ? "text-success-600" : "text-error-600"}
              >
                {erDelta >= 0 ? "+" : "−"}
                {formatNumber(Math.abs(erDelta) * 100, 1)} pp
              </Text>
            ) : null}
          </VStack>
        </HStack>
        <DeltaLine metric={data.reach} />
        <Sparkline
          points={data.daily.map((d) => ({ day: d.day, value: d.reach }))}
          campaignStartDay={campaignStartDay}
          secondary={{
            points: data.daily.map((d) => ({
              day: d.day,
              value: d.reach > 0 ? d.interactions / d.reach : 0,
            })),
            dashed: true,
          }}
        />
        <Text size="2xs" className="text-typography-400">
          Línea base · ER{" "}
          {hasEr ? formatPercentage(data.engagementRateBaseline) : "—"}
        </Text>
      </VStack>
    </Card>
  );
};

const LegendSwatch = ({
  color,
  dashed,
}: {
  color: string;
  dashed?: boolean;
}) => (
  <Box style={{ width: 18, height: 10 } as object}>
    <svg viewBox="0 0 18 10" width={18} height={10}>
      <line
        x1={0}
        y1={5}
        x2={18}
        y2={5}
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={dashed ? "3 3" : undefined}
      />
    </svg>
  </Box>
);

/**
 * Distinguishes "never connected" (offer the OAuth flow) from "connected but
 * the token stopped producing data" (reconnecting is what fixes it).
 */
const PlatformEmpty = ({
  platform,
  ig,
  tt,
  igAuthUrl,
  ttAuthUrl,
}: {
  platform: PlatformKey;
  ig: PlatformImpactFragmentFragment | null;
  tt: PlatformImpactFragmentFragment | null;
  igAuthUrl: string;
  ttAuthUrl: string;
}) => {
  const staleCopy =
    "La cuenta está vinculada pero no entregó métricas en esta ventana; su acceso probablemente expiró. Vuelve a conectarla para medir el impacto.";

  if (platform === "instagram") {
    return (
      <IntegrationCard
        heading={
          ig
            ? "Instagram no entregó datos en esta ventana"
            : "Conecta Instagram para medir el impacto en tu cuenta"
        }
        description={
          ig
            ? staleCopy
            : "Vincula la cuenta oficial de Instagram para cruzar followers ganados, alcance, visitas al perfil y clicks en el link contra la ventana de la campaña."
        }
        gradient={EMPTY_GRADIENT}
        ctas={[
          {
            label: ig ? "Reconectar Instagram" : "Conectar Instagram",
            href: igAuthUrl,
          },
        ]}
        logos={[InstagramLogo]}
      />
    );
  }
  if (platform === "tiktok") {
    return (
      <IntegrationCard
        heading={
          tt
            ? "TikTok no entregó datos en esta ventana"
            : "Conecta TikTok para medir el impacto en tu cuenta"
        }
        description={
          tt
            ? staleCopy
            : "Vincula la cuenta oficial de TikTok para cruzar followers ganados, alcance y bio link clicks contra la ventana de la campaña."
        }
        gradient={EMPTY_GRADIENT}
        ctas={[
          {
            label: tt ? "Reconectar TikTok" : "Conectar TikTok",
            href: ttAuthUrl,
          },
        ]}
        logos={[TikTokLogo]}
      />
    );
  }

  const anyLinked = !!ig || !!tt;
  return (
    <IntegrationCard
      heading={
        anyLinked
          ? "Tus cuentas no entregaron datos en esta ventana"
          : "Mide el impacto real en tus redes sociales"
      }
      description={
        anyLinked
          ? staleCopy
          : "Vincula las cuentas oficiales de Instagram y TikTok de la marca para cruzar followers, alcance y visitas al perfil contra la ventana de la campaña."
      }
      gradient={EMPTY_GRADIENT}
      ctas={[
        {
          label: ig ? "Reconectar Instagram" : "Conectar Instagram",
          href: igAuthUrl,
        },
        {
          label: tt ? "Reconectar TikTok" : "Conectar TikTok",
          href: ttAuthUrl,
        },
      ]}
      logos={[InstagramLogo, TikTokLogo]}
    />
  );
};

export default BrandImpactSection;
